import { apm } from "@elastic/apm-rum";
import { ShareLocation } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Feature } from "geojson";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { UseActiveLayer } from "shared/common/hooks/UseActiveLayer";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { getColumnDefinitionsFromLayer } from "shared/common/utils/layers/layerutils";
import { FeatureColumn } from "./FeatureListTable";
import SingleFeatureCell from "./SingleFeatureCell";

type row = Feature & { uniqueID?: number };

interface TableProps {
  selectedTableRows: row;
  showGeonisMask: boolean;
}

export default function SingleFeatureTable({
  selectedTableRows,
  showGeonisMask,
}: TableProps) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const changeLoading = defaultStore((state) => state.changeLoading);

  const editPopover = sidebarStore((state) => state.editPopover);
  const featureTableAddedRows = sidebarStore(
    (state) => state.featureTableAddedRows
  );
  const editToolActivated = sidebarStore((state) => state.editToolActivated);

  const activeLayer = UseActiveLayer(viewer);

  //move view to feature
  const handleMoveToFeature = () => {
    if (selectedTableRows == undefined) {
      changeAlertMessage({
        message: t("alert_error.missing_feature_image"),
        type: "error",
      });
      return;
    }

    changeLoading(true);
    if (
      selectedTableRows.geometry !== undefined &&
      selectedTableRows.geometry !== null
    ) {
      let point: number[][] | number[] | number | null = null;
      switch (selectedTableRows.geometry.type) {
        case "Point":
          point = selectedTableRows.geometry.coordinates;
          break;
        case "MultiPoint":
        case "LineString":
          point = selectedTableRows.geometry.coordinates[0];
          break;
        case "MultiLineString":
        case "Polygon":
          point = selectedTableRows.geometry.coordinates[0][0];
          break;
        case "MultiPolygon":
          point = selectedTableRows.geometry.coordinates[0][0][0];
          break;
      }

      if (Array.isArray(point) && point[0] !== undefined) {
        if (Array.isArray(point[0]) && point[0][2] === undefined) {
          viewer
            ?.movePositionLookAtPosition(
              {
                easting:
                  (Array.isArray(point) &&
                    !Array.isArray(point[0]) &&
                    point[0]) ||
                  0,
                northing:
                  (Array.isArray(point) &&
                    !Array.isArray(point[1]) &&
                    point[1]) ||
                  0,
              },
              100,
              viewer?.getScene().getCurrentGeoframe().srid
            )
            .then(() => {
              changeLoading(false);
            })
            .catch(() => {
              changeLoading(false);
              changeAlertMessage({
                message: t("alert_error.missing_feature_image"),
                type: "error",
              });
            });
        } else {
          viewer
            ?.movePositionLookAtPosition(
              {
                easting:
                  (Array.isArray(point) &&
                    !Array.isArray(point[0]) &&
                    point[0]) ||
                  0,
                northing:
                  (Array.isArray(point) &&
                    !Array.isArray(point[1]) &&
                    point[1]) ||
                  0,
                height:
                  (Array.isArray(point) &&
                    !Array.isArray(point[2]) &&
                    point[2]) ||
                  0,
              },
              100,
              viewer?.getScene().getCurrentGeoframe().srid
            )
            .then(() => {
              changeLoading(false);
            })
            .catch(() => {
              changeLoading(false);
              changeAlertMessage({
                message: t("alert_error.missing_feature_image"),
                type: "error",
              });
            });
        }
      } else {
        changeLoading(false);
        changeAlertMessage({
          message: t("alert_error.missing_feature_image"),
          type: "error",
        });
      }
    } else {
      changeLoading(false);
      changeAlertMessage({
        message: t("alert_error.missing_feature_image"),
        type: "error",
      });
    }
  };

  //update feature attributes
  const changeFeature = (value: string, item: Feature, columnName: string) => {
    if (
      editToolActivated === "add" &&
      editPopover &&
      featureTableAddedRows &&
      featureTableAddedRows.inserted &&
      selectedTableRows.uniqueID
    ) {
      const properties = {
        ...featureTableAddedRows.inserted[selectedTableRows.uniqueID]
          .properties,
      };
      properties[columnName] = value;
      featureTableAddedRows.inserted[selectedTableRows.uniqueID].properties =
        properties;
      selectedTableRows.properties = properties;
    } else {
      if (item.id && viewer) {
        if (activeLayer === null) return;
        // If the feature is already updated, use the already updated feature.
        // Otherwise the previous update will be lost.
        const updating_feature =
          activeLayer.getLayerEditor().featuresupdate[item.id] || item;
        updating_feature.properties[columnName] = value;
        activeLayer.getLayerEditor().updateFeatures(updating_feature);
      }
    }
  };

  // add feature to selection
  useEffect(() => {
    if (!viewer) return;
    if (!activeLayer) return;
    if (!selectedTableRows) return;
    if (!selectedTableRows.id) return;
    activeLayer.featureselector.addToSelection(String(selectedTableRows.id));
  }, [selectedTableRows, activeLayer]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
      }}
    >
      {!showGeonisMask && (
        <Table
          stickyHeader
          sx={{
            display: "block",
            overflow: "auto",
            height: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: "1%",
                  padding: "4px",
                  backgroundColor: darkTheme ? "darkblue.main" : "white",
                }}
              >
                <Typography
                  sx={{
                    color: darkTheme ? "white" : "darkblue.main",
                    fontSize: "14px",
                  }}
                >
                  {t("layers_window.feature_table_name_column")}
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  width: "1%",
                  padding: "4px",
                  backgroundColor: darkTheme ? "darkblue.main" : "white",
                }}
              >
                <Typography
                  sx={{
                    color: darkTheme ? "white" : "darkblue.main",
                    fontSize: "14px",
                  }}
                >
                  {t("layers_window.feature_table_value_column")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeLayer &&
              getColumnDefinitionsFromLayer(activeLayer).map(
                (item: FeatureColumn) => (
                  <TableRow
                    sx={{
                      "@media (hover: hover)": {
                        ":hover": { backgroundColor: "darkblue.light" },
                      },
                    }}
                    key={item.key}
                  >
                    <TableCell sx={{ padding: "4px" }}>
                      <Typography
                        sx={{
                          color: darkTheme ? "white" : "darkblue.main",
                          fontSize: "14px",
                        }}
                      >
                        {item.name}
                      </Typography>
                    </TableCell>
                    <SingleFeatureCell
                      selectedTableRows={selectedTableRows}
                      col={item}
                      item={
                        item.key != "id" && selectedTableRows.properties
                          ? selectedTableRows.properties[item.key]
                          : selectedTableRows["id"]
                      }
                      onChange={changeFeature}
                    />
                  </TableRow>
                )
              )}
          </TableBody>
        </Table>
      )}
      {showGeonisMask && (
        <Typography
          sx={{ color: darkTheme ? "white" : "darkblue.main" }}
          variant="body2"
        >
          {t("layers_window.geonis_mask_message")}
        </Typography>
      )}
      {!editPopover && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            gap: 1,
            paddingY: "4px",
            color: darkTheme ? "white" : "darkblue.main",
          }}
        >
          <Tooltip
            title={t("tooltip.feature_info_jump_to_feature")}
            placement="top"
            enterDelay={400}
            classes={{
              tooltip: "infra3d-buttonDescription",
              popper: "infra3d-buttonDescriptionPopperTop",
            }}
          >
            <IconButton
              onClick={() =>
                monitor(
                  apm,
                  "move-to-feature | Feature Info",
                  handleMoveToFeature
                )
              }
              sx={{
                color: darkTheme ? "white" : "darkblue.main",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
              }}
            >
              <ShareLocation />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
